// src/components/Screen1_UserInfo.jsx
import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { checkMobileDuplicate } from '../api'; // <-- Import new function

// Date of Birth validation function
function isValidDate(dateString) {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        return false; // Invalid format
    }

    const [day, month, year] = dateString.split('/').map(Number);
    
    // Basic checks
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    
    const date = new Date(year, month - 1, day);
    const currentYear = new Date().getFullYear();

    // Check if it's a real date and not some future date or excessively old date
    return (
        date.getDate() === day &&
        date.getMonth() === month - 1 &&
        date.getFullYear() === year &&
        year > 1900 && // Must be born after 1900
        year < currentYear // Must not be born in the future
    );
}

function Screen1_UserInfo() {
    const { showScreen, setUserInfo, currentScreen  } = useAppContext();
    
    const [formData, setFormData] = useState({
        title: '',
        name: '',
        mobile: '',
        email: '',
        dob: '', // <-- Changed from 'ic'
        terms: false,
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false); // <-- NEW loading state
    
    // Reset form state if we navigate back to screen-1
    useEffect(() => {
        if (currentScreen === 'screen-1') {
            setFormData({
                title: '',
                name: '',
                mobile: '',
                email: '',
                dob: '',
                terms: false,
            });
            setError('');
            setIsLoading(false);
        }
    }, [currentScreen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // NEW handler for DOB formatting
    const handleDobChange = (e) => {
        let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
        let formattedValue = '';

        if (value.length > 4) {
            formattedValue = `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4, 8)}`;
        } else if (value.length > 2) {
            formattedValue = `${value.substring(0, 2)}/${value.substring(2, 4)}`;
        } else {
            formattedValue = value;
        }
        
        setFormData(prev => ({ ...prev, dob: formattedValue }));

        if (formattedValue.length === 10 || formattedValue === '') {
            setError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true); // <-- Start loading
        setError('');
        
        if (!formData.terms) {
            setError("You must agree to the terms & conditions.");
            setIsLoading(false); // <-- Stop loading
            return;
        }
        
        // Check all fields (except terms)
        const { terms, ...dataToValidate } = formData;
        if (Object.values(dataToValidate).some(val => !val)) {
            setError("Please fill in all fields.");
            setIsLoading(false); // <-- Stop loading
            return;
        }

        // NEW: Date of Birth validation
        if (!isValidDate(formData.dob)) {
            setError("Please enter a valid Date of Birth (DD/MM/YYYY).");
            setIsLoading(false); // <-- Stop loading
            return;
        }

        // --- NEW: Server-side duplicate check ---
        const checkResult = await checkMobileDuplicate(formData.mobile);

        if (checkResult.error) {
            // Show network or server error
            setError(checkResult.error);
            setIsLoading(false); // <-- Stop loading
            return;
        }

        if (checkResult.isDuplicate) {
            // Show duplicate error
            setError("This mobile number has already been registered.");
            setIsLoading(false); // <-- Stop loading
            return;
        }
        // --- End of new duplicate check ---
        // All checks passed
        setError('');
        setUserInfo(dataToValidate); 
        showScreen('screen-2');
        // No need to set isLoading(false) here, as the component will unmount
    };

    // Dynamic class for the <select> placeholder
    const selectClasses = `mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
        formData.title ? 'text-black' : 'text-gray-400'
    } h-11`;

    return (
        <div id="screen-1" className="screen">
            <div className="flex items-center justify-center">
                <img src="/assets/screen_1_banner.jpg" alt="Car Banner" className="w-full h-50 object-cover shadow-md" />
            </div>

            <div className="screen_padding">
                <h2 className="text-2xl font-semibold text-center title_style">Rate your favourite MPV Car</h2>
                <p className="text-sm text-center mb-6">Enter your personal particular</p>
                
                <form id="user-form" className="space-y-4" onSubmit={handleSubmit}>
                    {/* ... (Title, Name, Mobile, Email inputs remain the same) ... */}
                    <div>
                        <select 
                            id="title" 
                            name="title" 
                            required 
                            className={selectClasses}
                            value={formData.title}
                            onChange={handleChange}
                        >
                            <option value="" disabled hidden>Title</option>
                            <option value="Mr.">Mr.</option>
                            <option value="Ms.">Ms.</option>
                            <option value="Dato">Dato</option>
                            <option value="Dato Sri">Dato Sri</option>
                            <option value="Tan Sri">Tan Sri</option>
                        </select>
                    </div>
                                                                                
                    <div>
                        <input type="text" id="name" name="name" placeholder="Name" required value={formData.name} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div>
                        <input type="tel" id="mobile" name="mobile" placeholder="Mobile" required value={formData.mobile} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div>
                        <input type="email" id="email" name="email" placeholder="Email" required value={formData.email} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    
                    {/* <-- UPDATED Field: DOB --> */}
                    <div>
                        <input 
                            type="tel" 
                            id="dob" 
                            name="dob" 
                            placeholder="Date of Birth (DD/MM/YYYY)" 
                            required 
                            inputMode="numeric" 
                            maxLength="10" 
                            value={formData.dob} 
                            onChange={handleDobChange} 
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    
                    {/* ... (Terms checkbox, error, and submit button remain the same) ... */}
                    <div className="flex items-center">
                        <input id="terms" name="terms" type="checkbox" required checked={formData.terms} onChange={handleChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                        <label htmlFor="terms" className="ml-2 block text-sm uppercase">
                            I agree your <a className="underline" target="_blank" href="terms.html" rel="noopener noreferrer">terms & conditions</a>
                        </label>
                    </div>

                    {error && <div id="form1-error" className="text-red-500 text-sm">{error}</div>}

                    <div className="flex justify-center">
                        <button 
                            type="submit" 
                            className="w-[250px] flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            disabled={isLoading} // <-- Disable on load
                        >
                            {isLoading ? (
                                <>
                                    <i className="fas fa-spinner fa-spin mr-2"></i> Checking...
                                </>
                            ) : (
                                'Submit'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default Screen1_UserInfo;